<!-- eslint-disable vue/no-v-html -->
<template>
	<div
		class="loading-screen"
		:style="{ '--loading-iterations': finalCycle }"
		:class="{ 'loading-static': skipCycle || reducedMotion === 'reduce' }"
		role="status"
	>
		<span class="loading-label">{{ label }}</span>
		<div class="loading-mark">
			<div v-html="logo" />
			<div
				ref="confettiContainer"
				class="loading-confetti pointer-events-none"
				:class="{ invisible: !confettiVisible }"
			/>
			<span
				class="loading-cue absolute"
				@animationstart="playConfetti"
				@animationiteration="playConfetti"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import lottie, { type AnimationItem } from 'lottie-web';
import { usePreferredReducedMotion } from '@vueuse/core';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
// Trusted checked-in markup also emitted by startupLoadingPlugin for hydration.
import logo from './logo.html?raw';
import './loading.css';
import { confettiPlaybackTime, delayToCycleEnd, LOADING_CYCLE_MS } from './loadingTiming';

const props = withDefaults(
	defineProps<{ label: string; ready?: boolean; skipCycle?: boolean; startedAt?: number }>(),
	{ ready: false, skipCycle: false, startedAt: 0 },
);
const emit = defineEmits<{ (event: 'complete'): void }>();
let completionTimer: ReturnType<typeof setTimeout> | null = null;

const finalCycle = ref<number>();
const reducedMotion = usePreferredReducedMotion();
watch(
	() => [props.ready, props.skipCycle, reducedMotion.value],
	() => {
		if (completionTimer !== null) clearTimeout(completionTimer);
		finalCycle.value = undefined;
		if (!props.ready) return;
		const elapsed = Math.max(0, performance.now() - props.startedAt);
		// Finish the current CSS iteration and hold its green end frame even if JS is busy.
		finalCycle.value = Math.max(1, Math.ceil(elapsed / LOADING_CYCLE_MS));
		const delay =
			props.skipCycle || reducedMotion.value === 'reduce' ? 0 : delayToCycleEnd(elapsed);
		completionTimer = setTimeout(() => emit('complete'), delay);
	},
	{ immediate: true },
);
onBeforeUnmount(() => {
	if (completionTimer !== null) clearTimeout(completionTimer);
});
const confettiContainer = ref<HTMLElement | null>(null);
const confettiVisible = ref(false);
let animation: AnimationItem | null = null;

function stopConfetti() {
	animation?.destroy();
	animation = null;
	confettiVisible.value = false;
}

function prepareConfetti() {
	stopConfetti();
	if (props.skipCycle || reducedMotion.value === 'reduce' || !confettiContainer.value) return;

	animation = lottie.loadAnimation({
		container: confettiContainer.value,
		renderer: 'svg',
		loop: false,
		autoplay: false,
		path: '/animation_small_confetti.json',
	});
	animation.setSpeed(1.5);
	// Hydration or asset loading may finish after the CSS cue has already fired.
	animation.addEventListener('DOMLoaded', playConfetti);
	animation.addEventListener('complete', () => {
		confettiVisible.value = false;
	});
	// Confetti is optional decoration; a failed asset must never block loading.
	animation.addEventListener('data_failed', stopConfetti);
}

function playConfetti() {
	if (props.skipCycle || reducedMotion.value === 'reduce' || !animation?.isLoaded) return;
	const time = confettiPlaybackTime(
		performance.now() - props.startedAt,
		animation.getDuration(false) * 1000,
	);
	if (time === null) return;
	confettiVisible.value = true;
	animation.goToAndPlay((time * animation.frameRate) / 1000, true);
}

onMounted(prepareConfetti);
watch(() => [props.skipCycle, reducedMotion.value], prepareConfetti);
onBeforeUnmount(stopConfetti);
</script>
